import { getCachedAPIResponse } from "../apiCaching/apiWrapper";
import { isValidZipCode } from "../apiCaching/POST-Requests/helperModules/helperFunctions";
import { apiConfig } from "../config/apiConfig";
import { REGEX } from "../constants/regex";
import { UseMyLocationSearch } from "../header";
import { getBrandFilter, formatCAPostalCode, isValidCAPostalCode, validateLocalZipPostalCode } from "../nbly-brand/helper_functions";
import { getSessionAndCorrelationID } from "../util/getSessionCorrelationId";
import { startLoader, stopLoader } from "../util/loader";
import { useMemo } from "../util/memoization";
import { checkAutoLocationOnLoad } from "../util/share";
import { fetchSearchTerms } from "./helperCalls";

interface ServiceData {
    proTypeId: string;
    proTypeName: string;
}

interface SearchTermData {
    serviceId: string;
    name: string;
}

export class SearchResult {
    private static instance: SearchResult;
    
    static getInstance(): SearchResult {
        if (!SearchResult.instance) {
            SearchResult.instance = new SearchResult();
        }
        return SearchResult.instance;
    }

    private readonly dropdown: HTMLElement | null = null;
    private readonly serviceInput: HTMLInputElement | null = null;
    private readonly zipInput: HTMLInputElement | null = null;
    private debounceTimer: NodeJS.Timeout | null = null;
    private readonly debounceDelay: number = 300; // 300ms debounce delay

    constructor() {
        this.dropdown = document.querySelector('.suggestion-container');
        this.serviceInput = document.querySelector('.service-input') as HTMLInputElement;
        this.zipInput = document.querySelector('.zip-input') as HTMLInputElement;
        this.zipInput.maxLength = 10;
        
        if (this.serviceInput) {
            this.setupEventListeners();
        }
        this.populateIfexistingZipExists();
        this.initAutoLocation();
    }

    private initAutoLocation(): void {
        const urlParams = new URLSearchParams(globalThis.location.search);
        const addressParam = urlParams.get("address") ?? '';
        if(!addressParam) {
            checkAutoLocationOnLoad('know_my_location', this.zipInput); 
        }
    }

    private setupEventListeners(): void {
        this.serviceInput?.addEventListener('focusin', () => this.handleServiceInputFocus());
        this.serviceInput?.addEventListener('keyup', (e) => this.handleSearchInputKeyup(e));
        
        // Add zipcode input validation
        this.zipInput?.addEventListener('input', () => this.handleZipcodeInput());
        
        // Add search button click validation
        const searchButton = document.querySelector('.new-hero-banner .search-btn') as HTMLElement;
        const mapIcon = document.querySelector('.zip-box .icon') as HTMLElement;

        searchButton?.addEventListener('click', (e) => this.handleSearchButtonClick(e));
        mapIcon?.addEventListener('click', (e) => this.handleMapIconClick(e));

        document.addEventListener('click', (e) => {
            const target = e.target as HTMLElement;
            if (!target?.closest('.relative.flex-1')) {
                this.hideDropdown();
            }
        });
    }

    populateIfexistingZipExists(){
        if(!this.zipInput || this.zipInput.value){
            return;
        }
        const storedZip = sessionStorage.getItem('brandZipCode');
        if(storedZip){
            this.zipInput.value = storedZip;
        }
    }

    private handleMapIconClick(e: Event): void {
        e.preventDefault();
        if( this.zipInput){
            new UseMyLocationSearch()?.getCurrentLocation(e, this.zipInput);
        }
    }

    private async handleServiceInputFocus(): Promise<void> {
        const zipCode = this.zipInput?.value?.trim();
        if (!zipCode) {
            this.showError('Please enter a zip code first');
            return;
        }
        const searchTerm = (document.getElementById('service-search-input')as HTMLInputElement)?.value?.trim() || '';
        if(searchTerm){
            if (this.debounceTimer) {
                clearTimeout(this.debounceTimer);
            }
            
            // Set new timer for debounced search
            this.debounceTimer = setTimeout(async () => {
                if (searchTerm.length >= Number(process.env.MINIMUM_SEARCH_LENGTH_NBLY || 3)) {
                    await this.performSearchTermLookup(searchTerm);
                } else {
                    this.hideDropdown();
                }
            }, this.debounceDelay);
            return;
        }
        try {
            startLoader();
            const services = await this.fetchServices(zipCode);
            
            if (services?.length > 0) {
                this.renderServiceSuggestions(services);
                this.setupPopularServiceSelection();
            } else {
                this.showError('No services found for this zip code');
            }
        } catch (error) {
            this.handleError('Failed to fetch services', error);
        } finally {
            stopLoader();
        }
    }

    private async fetchServices(zipCode: string): Promise<ServiceData[]> {
        const sessionData = getSessionAndCorrelationID();
        let formattedZip = formatCAPostalCode(zipCode);
        const countryId = isValidCAPostalCode(zipCode) ? 2 : 1;
        if(isValidCAPostalCode(zipCode) && !formattedZip.includes(' ')){
            formattedZip = formattedZip.slice(0, 3) + ' ' + formattedZip.slice(3);
        }
        const url = `${apiConfig.ServiceAPI}&ZipCode=${formattedZip}&countryid=${countryId}&ServiceModifier=4&take=12&correlationId=${sessionData.correlationID}&sessionId=${sessionData.sessionID}`;
        
        return await getCachedAPIResponse(url, 60);
    }

    private renderServiceSuggestions(services: ServiceData[]): void {
        const container = document.querySelector('#popular-services-dropdown') as HTMLElement;
        if (!container) return;

        const list = container.querySelector('ul') || this.createElement(container, 'ul');
        list.innerHTML = services
            .map(service => `
                <li class="suggestion-item">
                    <button id="${service.proTypeId}" type="button" class="service-pill-btn">
                        ${this.escapeHtml(service.proTypeName)}
                    </button>
                </li>
            `).join('');

        this.showDropdown(container);
    }

    private setupPopularServiceSelection(): void {
        this.dropdown?.querySelectorAll('button')?.forEach(btn => {
            btn.addEventListener('click', () => this.handleServiceSelection(btn as HTMLButtonElement));
        });
    }

    private async handleServiceSelection(btn: HTMLButtonElement): Promise<void> {
        this.hideDropdown();

        if (!this.serviceInput) return;

        const serviceName = btn.textContent?.trim() || '';
        this.serviceInput.value = serviceName;
        const brandFilter = useMemo(() => getBrandFilter(serviceName, this.zipInput?.value?.trim() || ''), [serviceName, this.zipInput?.value?.trim() || '']);
        if(brandFilter.length > 0) {
                this.populateBrandDetails(brandFilter);
                this.showBrandDetails();
            }else{
                this.hideBrandDetails();
        }
        try {
            const searchTerms = await fetchSearchTerms(serviceName, this.zipInput?.value?.trim() || '');
            
            if (searchTerms?.length > 0) {
                const data = sortResults(searchTerms, 'rank', true);
                this.renderPredictiveText(data);
                this.setupServiceSelection();
            } 
        } catch (error) {
            this.handleError('Failed to fetch search suggestions', error);
        }
    }


    private renderPredictiveText(services: SearchTermData[]): void {
        const suggestionContainer = document.querySelector('#popular-services-dropdown') as HTMLElement;
        const predictiveContainer = document.querySelector('#predictive-text-dropdown') as HTMLElement;

        if (!predictiveContainer) return;

        const searchTerm = this.serviceInput?.value?.trim().toLowerCase() || '';
        
        const list = predictiveContainer.querySelector('.predictive-list') || this.createElement(predictiveContainer, 'ul');
        list.innerHTML = services
            .map(service => {
                const highlightedName = this.highlightMatchedText(service.name, searchTerm);
                return `
                    <li class="predictive-item" serviceId="${service.serviceId}">
                        <svg aria-hidden="true" class="icon">
                            <use xlink:href="/brand/_assets/images/icons/hero-icon-sprite.svg#search-icon"></use>
                        </svg>
                        <span class="predictive-text">${highlightedName}</span>
                    </li>
                `;
            }).join('');

        this.showDropdown(predictiveContainer);
        suggestionContainer?.classList.add('hidden');
    }

    private highlightMatchedText(text: string, searchTerm: string): string {
        if (!searchTerm) return this.escapeHtml(text);
        
        const escapedText = this.escapeHtml(text);
        const regex = new RegExp(`(${searchTerm.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
        return escapedText.replace(regex, '<strong>$1</strong>');
    }

    private setupServiceSelection(): void {
        const container = document.querySelector('#predictive-text-dropdown .predictive-list');
        if(!container) return;
        container.addEventListener('click', async (event: Event) => {
            const predictiveItem = (event.target as HTMLElement)?.closest('.predictive-item') as HTMLElement;
            
            if (predictiveItem && this.serviceInput) {
                const serviceId = predictiveItem.getAttribute('serviceId') || '';
                this.serviceInput.dataset.serviceId = serviceId;
                this.serviceInput.value = predictiveItem.textContent?.trim() || '';
                this.hideDropdown();
                
                // Trigger the in new banner search button update
                 
                //  const hasNewHeroBanner = container?.closest('.new-hero-banner');
                // if (hasNewHeroBanner) {
                //     this.updateSearchButton();
                // }

                // Trigger the brand search API call only if container has parent class search-result-hero-banner
                const hasSearchResultParent = container?.closest('.search-result-hero-banner');
                if (hasSearchResultParent) {
                    await this.triggerBrandSearch();
                }
            }
        });
    }

    private async triggerBrandSearch(): Promise<void> {
        const zipCode = this.zipInput?.value?.trim();
        const serviceName = this.serviceInput?.value?.trim();
        const serviceId = this.serviceInput?.dataset.serviceId;
        
        if (!zipCode || !serviceName) {
            return;
        }
        
        const isZipValid = validateLocalZipPostalCode(zipCode);
        if (!isZipValid) {
            this.showZipcodeError();
            return;
        }
        
        const formattedZip = formatCAPostalCode(zipCode);
        // Use BrandResult's performBrandSearch method
        const { BrandResult } = await import('./brand-result');
        const brandResult = BrandResult.getInstance();
        await brandResult.performBrandSearch(serviceName, formattedZip, serviceId);
    }

    private updateSearchButton(): void {
        const searchButton = document.querySelector('.new-hero-banner .search-btn') as HTMLAnchorElement;
        if (!searchButton || !this.serviceInput) return;

        const serviceId = this.serviceInput.dataset.serviceId || '';
        const zipCode = this.zipInput?.value?.trim().toUpperCase() || '';
        
        // Validate zipcode based on current site (CA site cannot use US zipcodes)
        if (!validateLocalZipPostalCode(zipCode)) {
            console.log("Zipcode validation failed for current site");
            this.showZipcodeError();
            return;
        }
        
        const serviceName = this.serviceInput.value?.trim() || '';    
        const isCA = isValidCAPostalCode(zipCode);
        const searchPath = isCA ? '/ca/search-result' : '/search-result';
        let formattedZip = zipCode;
        if(isValidCAPostalCode(zipCode) && !formattedZip.includes(' ')){
            formattedZip = formattedZip.slice(0, 3) + ' ' + formattedZip.slice(3);
        }
        const searchUrl = `${window.location.origin}${searchPath}?service=${serviceId}&address=${encodeURIComponent(formattedZip)}&name=${encodeURIComponent(serviceName)}&cacheBuster=${Date.now()}`;

        const section = document.querySelector('.new-search-result-section') as HTMLElement;
            if(section){
            const newSearchResultsList = document.querySelectorAll('.new-search-results li');
            const parentContainer = document.querySelector('.nbly-search-results .container');
            if(newSearchResultsList?.length > 0 && parentContainer){
                parentContainer.classList.add('hidden');
            }
         }

        window.location.replace(searchUrl);
    }

    private showDropdown(container: HTMLElement): void {
        this.dropdown?.classList.remove('hidden');
        container?.classList.remove('hidden');
    }

    private hideDropdown(): void {
        document.querySelector('#predictive-text-dropdown')?.classList.add('hidden');
        document.querySelector('#popular-services-dropdown')?.classList.add('hidden');
        this.dropdown?.classList.add('hidden');
    }

    private handleError(message: string, error: any): void {
        console.error(`${message}:`, error);
        this.showError(message);
        this.hideDropdown();
    }

    private showError(message: string): void {
        // You can implement a toast notification or error display here
        console.warn(message);
    }

    private escapeHtml(text: string): string {
        const div = document.createElement('div');
        div.textContent = text;
        return div.innerHTML;
    }

    private createElement(parent: HTMLElement, tagName: string): HTMLElement {
        const element = document.createElement(tagName);
        parent.appendChild(element);
        return element;
    }

    private handleSearchButtonClick(event: Event): void {

        const zipCode = this.zipInput?.value?.trim();

        const legacyZipCheck = isValidZipCode(zipCode);
        const isZipValid = zipCode !== '' && validateLocalZipPostalCode(zipCode);
        const zipFail = isZipValid && zipCode && legacyZipCheck && legacyZipCheck !== '00000' && zipCode.length > 0;
        
        if (!zipFail) {
            event.preventDefault();
            this.showZipcodeError();
            return;
        }else {
            this.updateSearchButton();
        }
        
        this.hideZipcodeError();
    }

    private handleZipcodeInput(): void {
        const zipCode = this.zipInput?.value?.trim();
        
        if (zipCode && zipCode.length > 0 && this.isValidZipCode(zipCode)) {
            this.hideZipcodeError();
        }
    }

    private showZipcodeError(): void {
        const errorElement = document.getElementById('zip-error-msg');
        if (errorElement) {
            errorElement.innerText = 'Please enter a valid zipcode';
            errorElement.classList.remove('hidden');
        }
    }

    private hideZipcodeError(): void {
        const errorElement = document.getElementById('zip-error-msg');
        if (errorElement) {
            errorElement.classList.add('hidden');
        }
    }

    private isValidZipCode(zipCode: string): boolean {
        // US zip code: 5 digits or 5 digits + 4 digits (12345 or 12345-6789)
        const usZipRegex = REGEX.sendZip;
        // Canadian postal code: A1A 1A1 or A1A1A1
        const canadianPostalRegex = REGEX.caSendZip;
        
        return usZipRegex.test(zipCode) || canadianPostalRegex.test(zipCode);
    }

    private handleSearchInputKeyup(event: KeyboardEvent): void {
        const searchTerm = (event.target as HTMLInputElement)?.value?.trim();
        
        // Clear previous timer
        if (this.debounceTimer) {
            clearTimeout(this.debounceTimer);
        }
        
        // Set new timer for debounced search
        this.debounceTimer = setTimeout(async () => {
            if (searchTerm && searchTerm.length >= Number(process.env.MINIMUM_SEARCH_LENGTH_NBLY || 3)) {
                await this.performSearchTermLookup(searchTerm);
            } else {
                this.hideDropdown();
            }
        }, this.debounceDelay);
    }

    private async performSearchTermLookup(searchTerm: string): Promise<void> {
        const zipCode = this.zipInput?.value?.trim();
        
        if (!zipCode) {
            this.showZipcodeError();
            return;
        }
        
        try {
            startLoader();
            const formattedZip = formatCAPostalCode(zipCode);
            const searchTerms = await fetchSearchTerms(searchTerm, formattedZip);
            
            if (searchTerms && searchTerms?.length > 0) {
                const data = sortResults(searchTerms, 'rank', true);
                this.renderPredictiveText(data);
                this.setupServiceSelection();
            } else {
                this.hideDropdown();
            }
        } catch (error) {
            this.handleError('Failed to fetch search terms', error);
        } finally {
            const brandFilter = useMemo(() => getBrandFilter(searchTerm, zipCode), [searchTerm, zipCode]);
            if(brandFilter.length > 0) {
                this.populateBrandDetails(brandFilter);
                this.showBrandDetails();
            }else{
                this.hideBrandDetails();
            }
            stopLoader();
        }
    }

    populateBrandDetails(brandFilter: string[]) {
        
        const brandDropdown = document.querySelector('#predictive-text-dropdown .brand-suggestions .brand-list') as HTMLUListElement;
        if(brandDropdown) {
            brandDropdown.innerHTML = brandFilter.join('');
        }
    }
    showBrandDetails() {
        const brandContainer = document.querySelector('.suggestion-container .brand-suggestions') as HTMLDivElement;
        if(brandContainer) {
            brandContainer.classList.remove('hidden');
        }
        const suggestionContainer = document.querySelector('.suggestion-container') as HTMLDivElement;
        if(suggestionContainer) {
            suggestionContainer.classList.remove('hidden');
        }
        const predictiveContainer = document.querySelector('#predictive-text-dropdown') as HTMLUListElement;
        if(predictiveContainer) {
            predictiveContainer.classList.remove('hidden');
        }
        
    }
    hideBrandDetails() {
        const brandContainer = document.querySelector('.suggestion-container .brand-suggestions') as HTMLDivElement;
        if(brandContainer) {
            brandContainer.classList.add('hidden');
        }
    }
}



export function sortResults(result: any, prop: any, asc: any)
{
    result.sort(function (a: any, b: any)
    {
        if (asc)
        {
            return (a[prop] > b[prop]) ? 1 : ((a[prop] < b[prop]) ? -1 : 0);
        }
        else
        {
            return (b[prop] > a[prop]) ? 1 : ((b[prop] < a[prop]) ? -1 : 0);
        }
    });
    return result;
}

document.addEventListener('DOMContentLoaded', () => {
    const newHeroBanner = document.querySelector(".new-hero-banner");
    const searchResultForm = document.querySelector(".search-result-hero-banner");
    
    if (newHeroBanner || searchResultForm) {
        SearchResult.getInstance();
    }
});